import { isPlatformBrowser } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import {
  afterNextRender,
  ChangeDetectorRef,
  DestroyRef,
  effect,
  Injector,
  PLATFORM_ID,
  runInInjectionContext,
  signal,
  WritableSignal
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormGroup } from "@angular/forms";
import { PermissionNamesEnum } from "@server/services/cbox/public/api/v1/enforcers/entity_permission/types";
import { CBox_GetAgentListIdentifierResponse } from "@server/services/cbox/public/api/v1/resources/agent/types";
import { CBox_PublicPaginatedResponse } from "@server/services/cbox/public/api/v1/resources/common/request_base/types";
import { CBox_GetLockerBaseData, CBox_GetLockerShortData } from "@server/services/cbox/public/api/v1/resources/locker/types";
import { TablePageEvent } from "primeng/table";
import { debounceTime, of, Subject } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { lockerCleanAddress } from "src/helpers/functions";
import { AgentsService } from "src/services/agents/agents.service";
import { ApiService, handlePublicApiError } from "src/services/api/api.service";
import { CacheService } from "src/services/cache/cache.service";
import { ConfigurationService } from "src/services/configuration/configuration.service";
import { LockersService } from "src/services/lockers/lockers.service";
import { ReportsService } from "src/services/reports/reports.service";
import { SessionService } from "src/services/session/session.service";
import { ToastService } from "src/services/toast/toast.service";

export abstract class PaginatedTableWithFilters<T, SearchParamsType> {

  private readonly FILTER_DATE_THRESHOLD = 1000 * 60 * 60 * 24;

  data = signal<T[]>([]);
  loading = signal(true);
  autoRefreshing = signal(false);
  totalCount = signal(0);
  lockers = signal<CBox_GetLockerShortData[] | undefined>(undefined);
  agents = signal<CBox_GetAgentListIdentifierResponse | undefined>([]);
  permissions = PermissionNamesEnum;

  lockersService: LockersService;
  agentsService: AgentsService;
  api: ApiService;
  toastService: ToastService;
  configurationService: ConfigurationService;
  sessionService: SessionService;
  cacheService: CacheService;
  reportsService: ReportsService;
  changeDetectorRef: ChangeDetectorRef;
  destroyRef: DestroyRef;

  abstract filtersForm: FormGroup;
  abstract sessionFiltersKey: string;
  abstract sessionFiltersToSave: string[];
  abstract localStorageFiltersKey: string;
  abstract localStorageFiltersToSave: string[];
  abstract dataFetchUrl: string;
  abstract defaultFilters: WritableSignal<Record<string, any>>;

  today = new Date();
  oneMonthAgo = new Date();

  private lastImmediateFetch = signal<number>(0);
  bypassAutoRefresh = signal<boolean>(false);
  private readonly IMMEDIATE_FETCH_THROTTLE_TIME = signal(10000);

  protected autoRefreshData = signal(false);
  protected dataFetchTrigger = new Subject<void>();

  constructor(private injector: Injector) {
    this.today.setHours(23, 59, 59, 999);
    this.oneMonthAgo.setMonth(this.today.getMonth() - 1);
    this.oneMonthAgo.setHours(0, 0, 0, 0);
    this.lockersService = injector.get(LockersService);
    this.agentsService = injector.get(AgentsService);
    this.toastService = injector.get(ToastService);
    this.api = injector.get(ApiService);
    this.sessionService = injector.get(SessionService);
    this.cacheService = injector.get(CacheService);
    this.configurationService = injector.get(ConfigurationService);
    this.reportsService = injector.get(ReportsService);
    this.changeDetectorRef = injector.get(ChangeDetectorRef);
    this.destroyRef = injector.get(DestroyRef);
    afterNextRender(() => {
      window.scrollTo(0, 0);
      this.baseInit();
    });
  }

  async baseInit(): Promise<void> {
    if (isPlatformBrowser(this.injector.get(PLATFORM_ID))) {
      this.lockers.set(await this.lockersService.getList());
      this.agents.set(await this.agentsService.getList());
      this.patchCachedFilters();
      this.watchFiltersChanges();
      await this.init();
      this.setupDataFetchStream();
      this.dataFetchTrigger.next();
      this.setupPageFetchSignal();
      this.changeDetectorRef.detectChanges();
    }
  }

  watchFiltersChanges(): void {
    let previousFiltersValue = this.filtersForm.getRawValue();

    this.filtersForm.valueChanges
      .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.bypassAutoRefresh.set(false);

        const currentNonPage = { ...value, page: undefined };
        const previousNonPage = { ...previousFiltersValue, page: undefined };

        if (JSON.stringify(currentNonPage) !== JSON.stringify(previousNonPage)) {
          // A non-page filter changed. Reset page to 1
          if (value.page !== 1) {
            this.filtersForm.patchValue({ page: 1 }, { emitEvent: false });
            value.page = 1;
          }
        }

        previousFiltersValue = this.filtersForm.getRawValue();

        if (value.interval) {
          value.interval = value.interval.map((date: Date, index: number) => {
            const modified = new Date(date).setHours(
              index === 0 ? 0 : 23,
              index === 0 ? 0 : 59,
              59,
              999
            );
            return new Date(modified);
          });
        }
        this.saveFilters();
        this.onFiltersChange();
      });
  }

  fetchData(): void {
    if (!isPlatformBrowser(this.injector.get(PLATFORM_ID))) {
      return;
    }
    this.dataFetchTrigger.next();
  }

  private setupDataFetchStream(): void {
    this.dataFetchTrigger
      .pipe(
        switchMap(() => {
          return this.api.get<CBox_PublicPaginatedResponse<T>>(
            "backend/" + this.dataFetchUrl,
            this.getSearchStructure()
          ).pipe(
            catchError((error: HttpErrorResponse) => {
              this.loading.set(false);
              this.autoRefreshing.set(false);
              this.bypassAutoRefresh.set(true);
              handlePublicApiError(error, this.toastService);
              return of({
                success: true,
                data: this.data(),
                totalCount: this.totalCount(),
              });
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response) => {
          console.log(response, "RESPONSE");
          this.peekData(response.data);
          this.data.set(response.data);
          this.totalCount.set(response.totalCount);
          this.loading.set(false);
          this.autoRefreshing.set(false);
        }
      });
  }

  peekData(data: T[]): void {}

  protected pageChanged(event: TablePageEvent): void {
    const page = event.first / event.rows;
    this.filtersForm.patchValue({
      page: page + 1,
      pageSize: event.rows
    });
    window.scrollTo(0, 0);
    this.saveFilters();
  }

  protected saveFilters(): void {
    this.saveSessionFilters();
    this.saveLocalStorageFilters();
  }

  protected onFiltersChange(): void {
    if (this.areBothDatesSelected()) {
      this.loading.set(true);
      this.dataFetchTrigger.next();
    }
  }

  protected areBothDatesSelected(): boolean {
    if (!this.filtersForm.get("interval")) {
      return true;
    }
    const interval = this.filtersForm.get("interval")?.value;
    return interval && interval.length === 2 && interval[0] && interval[1];
  }

  protected saveSessionFilters(): void {
    const sessionFilters: Record<string, any> = {};

    for (const key of this.sessionFiltersToSave) {
      if (key === "interval") {
        // Serialize only the interval key
        const interval = this.filtersForm.get(key)?.value;
        sessionFilters[key] = interval
          ? interval.map((date: Date) => (date instanceof Date ? date.getTime() : date))
          : interval;
      } else {
        sessionFilters[key] = this.filtersForm.get(key)?.value;
      }
    }

    sessionFilters["lastAccessedAt"] = new Date();

    sessionStorage.setItem(this.sessionFiltersKey, JSON.stringify(sessionFilters));
  }

  protected saveLocalStorageFilters(): void {
    const localStorageFilters: Record<string, any> = {};

    for (const key of this.localStorageFiltersToSave) {
      if (key === "interval") {
        // Serialize only the interval key
        const interval = this.filtersForm.get(key)?.value;
        localStorageFilters[key] = interval
          ? interval.map((date: Date) => (date instanceof Date ? date.getTime() : date))
          : interval;
      } else {
        localStorageFilters[key] = this.filtersForm.get(key)?.value;
      }
    }

    localStorage.setItem(this.localStorageFiltersKey, JSON.stringify(localStorageFilters));
  }

  protected patchCachedFilters(): void {
    if (!this.filtersForm) {
      console.warn("Filters form not initialized");
      return;
    }
    const defaultFilters = this.defaultFilters();

    const sessionFilters = this.sessionService.getItem(this.sessionFiltersKey, undefined);
    const storageFilters = this.cacheService.getVal(this.localStorageFiltersKey, undefined);

    let parsedFilters;
    let parsedStorageFilters;

    if (sessionFilters) {
      try {
        parsedFilters = JSON.parse(sessionFilters);
        if (parsedFilters.lastAccessedAt) {
          const lastAccessedAt = new Date(parsedFilters.lastAccessedAt);
          if (new Date().getTime() - lastAccessedAt.getTime() > this.FILTER_DATE_THRESHOLD) {
            delete parsedFilters["interval"];
            delete parsedFilters["lastAccessedAt"];
          }
        }
        if (parsedFilters.interval) {
          parsedFilters.interval = parsedFilters.interval.map((item: number, index: number) => {
            return index === 0 ? this.startOfDay(new Date(item)) : this.endOfDay(new Date(item));
          });
        }
      } catch (e) {
        console.error("Error parsing session filters:", e);
      }
    }

    if (storageFilters) {
      try {
        parsedStorageFilters = JSON.parse(storageFilters);
        if (parsedStorageFilters.interval) {
          parsedStorageFilters.interval = parsedStorageFilters.interval.map((item: number, index: number) => {
            return index === 0 ? this.startOfDay(new Date(item)) : this.endOfDay(new Date(item));
          });
        }
      } catch (e) {
        console.error("Error parsing storage filters:", e);
      }
    }

    this.filtersForm.patchValue(
      {
        ...defaultFilters,
        ...parsedFilters,
        ...parsedStorageFilters,
      },
      { emitEvent: false }
    );
  }

  protected formatLockerAddress(address: CBox_GetLockerBaseData["addressParts"]): string {
    return lockerCleanAddress(address);
  }

  abstract init(): Promise<void>;

  abstract getSearchStructure(): Partial<SearchParamsType>;

  protected hasAccessTo(permission: PermissionNamesEnum[]): boolean {
    return this.configurationService.hasAccess(permission);
  }

  private setupPageFetchSignal(): void {
    if (!isPlatformBrowser(this.injector.get(PLATFORM_ID))) {
      return;
    }

    const windowFocused = signal(document.hasFocus());

    const onFocus = () => windowFocused.set(true);
    const onBlur = () => windowFocused.set(false);
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);

    let fetchTimer: number | null = null;

    this.destroyRef.onDestroy(() => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
      if (fetchTimer !== null) {
        clearInterval(fetchTimer);
        fetchTimer = null;
      }
    });

    runInInjectionContext(this.injector, () => {
      effect(() => {
        if (this.autoRefreshData() && windowFocused() && !this.bypassAutoRefresh()) {
          const now = Date.now();
          if (now - this.lastImmediateFetch() >= this.IMMEDIATE_FETCH_THROTTLE_TIME()) {
            this.autoRefreshing.set(true);
            this.dataFetchTrigger.next();
            this.lastImmediateFetch.set(now);
          }
          if (fetchTimer === null) {
            fetchTimer = window.setInterval(() => {
              if (!this.bypassAutoRefresh()) {
                this.autoRefreshing.set(true);
                this.dataFetchTrigger.next();
                this.lastImmediateFetch.set(Date.now());
              }
            }, this.IMMEDIATE_FETCH_THROTTLE_TIME());
          }
        } else {
          if (fetchTimer !== null) {
            clearInterval(fetchTimer);
            fetchTimer = null;
          }
        }
      }, { allowSignalWrites: true });
    });
  }

  private startOfDay(date: Date): Date {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  }

  private endOfDay(date: Date): Date {
    const newDate = new Date(date);
    newDate.setHours(23, 59, 59, 999);
    return newDate;
  }

  protected patchFilters(value: any): void {
    this.filtersForm.patchValue(value);
  }

}